import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Script from 'next/script';
import {
  ReferFriendsAuthenticate,
  ReferFriendsContextValue,
  ReferFriendsProviderProps,
  ReferFriendsTrackCustomer
} from './types';
import { useIsEnabled } from '../../../shared/hooks/useIsEnabled';
import { Feature } from '../../../shared/utils/featureToggle';
import { FRIENDBUY_MERCHANT_ID, referFriendsButtonIdSchema } from './constants';
import { useStoreValue } from '../../../state/hooks/useStore';
import { getReferFriendBoardId } from './utils/getReferFriendBoardId';
import { useLocale } from '../../../shared/hooks/useLocale';
import {
  getAgReferFriendsBannerClosed,
  setAgReferFriendsBannerClosed
} from '../../../state/cookies/agReferFriendsBanner';
import { storeCodeSchema } from '../../../schema/storeCode';
import { useMutationReferFriend } from '../../../state/internal/referFriend/referFriend';

const { eu, uk, us, qa, ukd, eud } = storeCodeSchema.enum;

const ReferFriendsContext = createContext<ReferFriendsContextValue | null>(null);

const ReferFriendsProvider = ({ children }: ReferFriendsProviderProps) => {
  const isReferFriendsEnabled = useIsEnabled(Feature.referFriends);
  const store = useStoreValue();
  const locale = useLocale();
  const merchantId = FRIENDBUY_MERCHANT_ID[store];
  const [{ isFriendbuyLoaded, isCampaignsLoaded }, setIsLoaded] = useState({
    isFriendbuyLoaded: false,
    isCampaignsLoaded: false
  });
  const isLoaded = isFriendbuyLoaded && isCampaignsLoaded;
  const [showBanner, setShowBanner] = useState(false);
  const { mutate: authenticate } = useMutationReferFriend();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const isParticipatingStore = useMemo(
    () =>
      store === eu ||
      store === uk ||
      store === us ||
      store === qa ||
      store === ukd ||
      store === eud,
    [store]
  );

  const handleTrackCustomer = useCallback<ReferFriendsTrackCustomer>(({ id, email }) => {
    window.friendbuyAPI.push(['track', 'customer', { id, email }]);
  }, []);

  const handleAuthenticate = useCallback<ReferFriendsAuthenticate>(
    (args) => {
      authenticate(args, {
        onSuccess: ({ authString, signature }) => {
          window.friendbuyAPI.push(['auth', authString, signature]);
          setIsAuthenticated(true);
        }
      });
    },
    [authenticate]
  );

  const handleCloseBanner = useCallback(() => {
    setAgReferFriendsBannerClosed(true);
    setShowBanner(false);
  }, []);

  useEffect(() => {
    window.friendbuyAPI = window.friendbuyAPI || [];

    // registers your merchant using your merchant ID found in the
    // retailer app https://retailer.fbot.me/settings/general
    window.friendbuyAPI.push(['merchant', merchantId]);
  }, [merchantId]);

  useEffect(() => {
    const isBannerClosed = getAgReferFriendsBannerClosed();

    if (isParticipatingStore && !isBannerClosed) {
      setShowBanner(true);
    }
  }, [isParticipatingStore]);

  const value = useMemo<ReferFriendsContextValue>(
    () => ({
      isLoaded,
      getBoardId: (isPortal: boolean) => getReferFriendBoardId({ isPortal, store, locale }),
      elementId: referFriendsButtonIdSchema.enum.friendbuyoverlay,
      showBanner,
      isAuthenticated,
      handleTrackCustomer: isLoaded ? handleTrackCustomer : null,
      handleAuthenticate: isLoaded ? handleAuthenticate : null,
      handleCloseBanner
    }),
    [
      handleAuthenticate,
      handleCloseBanner,
      handleTrackCustomer,
      isAuthenticated,
      isLoaded,
      locale,
      showBanner,
      store
    ]
  );

  return (
    <ReferFriendsContext.Provider value={value}>
      {children}
      {merchantId && isReferFriendsEnabled && (
        <>
          <Script
            src="https://static.fbot.me/friendbuy.js"
            strategy="afterInteractive"
            onReady={() => setIsLoaded((prev) => ({ ...prev, isFriendbuyLoaded: true }))}
          />
          <Script
            src={`https://campaign.fbot.me/${merchantId}/campaigns.js`}
            strategy="afterInteractive"
            onReady={() => setIsLoaded((prev) => ({ ...prev, isCampaignsLoaded: true }))}
          />
        </>
      )}
    </ReferFriendsContext.Provider>
  );
};

function useReferFriends() {
  const context = useContext(ReferFriendsContext);

  if (!context) {
    throw new Error('useReferFriends must be used within a ReferFriendsProvider');
  }

  return context;
}

export default ReferFriendsContext;

export { ReferFriendsProvider, useReferFriends };
